import { h } from "preact";
import { useEffect, useMemo, useState } from "preact/hooks";
import {
    checkIfCanPrint,
    generate,
    labelsMetadata,
    LabelTemplateFields,
    PrinterInfo as PrinterInfoType,
    slavePrint,
} from "../api/label-creator";
import { egzotechAgentManager } from "../App";
import { useCommandHandler } from "../hooks";
import { Command } from "../hooks/Command";
import { Printing } from "./Printing";
import { PrinterInfo } from "./PrinterInfo";
import { TemplateQuickInfo } from "./TemplateQuickInfo";

const tapeType: { [key: string]: string | undefined } = {
    "1": "laminated, strong adhesive, security laminated, flexible ID tape",
    "3": "security, heat shrink",
    "4": "fabric tape",
    "20": "flexible id, starry laminated",
};

const printErrors = {
    "invalid-tape-width": "Szerokość włożonej taśmy nie zgadza się z szerokością podaną w szablonie",
    "no-printer": "Brak komunikacji z drukarką",
    "no-template": "Brak wybranego szablonu",
};

export function parseTapeType(tapeWidth?: string, tapeType?: string) {
    const tapeWidthNumber = parseInt(tapeWidth ?? "", 10);

    if (!tapeType || isNaN(tapeWidthNumber) || !isFinite(tapeWidthNumber) || tapeWidthNumber < 0) {
        return null;
    }

    const tapeTypeRegex = /(.*)ink\s+on(.*)tape.*?([a-zA-Z0-9 ]+)/gm;
    const result = tapeTypeRegex.exec(tapeType);

    if (!result) {
        return null;
    }

    return `${tapeWidthNumber} ${result[1].trim().replace(/\s+/gm, "_")}_on_${result[2].trim().replace(/\s+/gm, "_")} ${result[3].trim().replace(/\s+/gm, "_")}.jpg`;
}

export function TemplatePrint(props: {
    template: string;
    fields: LabelTemplateFields;
    overrideFields?: { [key: string]: string };
    copies?: number;
    doRefresh?: Command;
    noAutoCut?: boolean;
    tapeImgBase?: string;
    onPrint?: () => void;
    onProvideFields?: (
        index: number,
    ) =>
        | { fields: LabelTemplateFields; overrideFields?: { [key: string]: string } }
        | Promise<{ fields: LabelTemplateFields; overrideFields?: { [key: string]: string } }>;
    eachProvideFieldsCopy?: number;
}) {
    const [fetchedTemplateMetadata, setTemplateMetadata] = useState(null as null | { [key: string]: string });
    const [templateLength, setTemplateLength] = useState("");
    const [forceAllow, setForceAllow] = useState(false);
    const [printerInfo, setPrinterInfo] = useState(null as PrinterInfoType | null);

    useEffect(() => {
        labelsMetadata(props.template).then(r => setTemplateMetadata(r));
        // fetch(labelPreviewUrl(props.template)).then(r => r.text().then(r => setTemplateLength((/viewBox="\d+ \d+ (\d+) \d+"/i.exec(r) ?? ["", ""])[1])));
    }, [props.template]);

    const templateMetadata = useMemo(
        () =>
            fetchedTemplateMetadata
                ? {
                      ...fetchedTemplateMetadata,
                      ...props.overrideFields,
                  }
                : null,
        [fetchedTemplateMetadata, props.overrideFields],
    );

    const tapeTypeUrl = templateMetadata
        ? parseTapeType(templateMetadata["tape-width"], templateMetadata["tape-type"])
        : null;
    const tapeImg = tapeTypeUrl ? `${props.tapeImgBase}${tapeTypeUrl}` : "";

    const allowPrint =
        templateMetadata && printerInfo ? forceAllow || checkIfCanPrint(templateMetadata, printerInfo) : false;

    return (
        <div class="template-print">
            <div class="printer-template-preview">
                <div class="printer-template">
                    <div class="paper-2 printer-info">
                        <h2>Informacje o drukarce</h2>
                        <PrinterInfo onChange={info => setPrinterInfo(info)} />
                    </div>
                    <div class="paper-2 template-info">
                        <h2>Informacje o szablonie</h2>
                        <TemplateQuickInfo
                            templateMetadata={templateMetadata}
                            templateLength={templateLength}
                            fields={props.fields}
                        />
                    </div>
                </div>
                <div class="preview paper-2">
                    <h2>Podgląd</h2>
                    <div>
                        <img src={generate(props.template, props.fields, props.overrideFields)} />
                    </div>
                </div>
            </div>
            <div class="print-tape-img">
                <div class="print paper-2" style="margin-top: 10px;">
                    <h2>Akcje</h2>
                    <Printing
                        allowPrint={allowPrint === true}
                        copies={props.copies}
                        noAutoCut={props.noAutoCut}
                        onPrint={async (copies, noAutoCut) => {
                            if (props.onProvideFields) {
                                for (let i = 0; i < copies; i++) {
                                    let fieldsData = props.onProvideFields(i);

                                    if ("then" in fieldsData) {
                                        fieldsData = await fieldsData;
                                    }

                                    const eachCopies = props.eachProvideFieldsCopy ?? 1;

                                    for (let j = 0; j < eachCopies; j++) {
                                        await slavePrint(
                                            egzotechAgentManager.getAgentWithModule("label-printer")!,
                                            props.template,
                                            fieldsData.fields,
                                            {
                                                copies:
                                                    i === copies - 1 && j === eachCopies - 1
                                                        ? 1
                                                        : i === 0 && j === 0
                                                          ? -2
                                                          : -1,
                                                noAutoCut,
                                            },
                                            fieldsData.overrideFields,
                                        );
                                    }
                                }
                            } else {
                                await slavePrint(
                                    egzotechAgentManager.getAgentWithModule("label-printer")!,
                                    props.template,
                                    props.fields,
                                    { copies, noAutoCut },
                                    props.overrideFields,
                                );
                            }

                            if (props.onPrint) {
                                props.onPrint();
                            }
                        }}
                    />
                    {typeof allowPrint === "string" ? (
                        <div
                            onClick={e => {
                                if (e.detail === 3) {
                                    setForceAllow(!forceAllow);
                                }
                            }}
                        >
                            <div class="alert error">{printErrors[allowPrint]}</div>
                        </div>
                    ) : null}
                </div>
                {tapeImg ? (
                    <div class="tape-img paper-2">
                        <h2>Rodzaj taśmy</h2>
                        <div class="preview">
                            <img src={tapeImg} />
                        </div>
                    </div>
                ) : null}
            </div>
        </div>
    );
}
